<script setup lang="ts">
import { inject, useAttrs } from 'vue';
import { logger } from '@pkg/logger';
import { PROVIDE_FORM } from '../composables/useDynamicForm';
import { type FieldSchema } from '../../types';
import { cn } from '@pkg/ui/lib/utils';
import TurnstileFormShell from './TurnstileFormShell.vue';
import { TurnstileStatusValue } from '../../types';
const dynamicFormProvider = inject(PROVIDE_FORM);

if (!dynamicFormProvider) {
  logger.error('DynamicForm should only be used in a component that also calls useDynamicForm() to set it up');
}

const { form, dynamicFormSchema } = dynamicFormProvider || { form: null, dynamicFormSchema: null };

function bindFields(field: FieldSchema): Omit<FieldSchema, 'component' | 'validation'> {
  const newField: Partial<FieldSchema> = { ...field };
  // Not needed at component level, therefore we delete it
  delete newField.component;
  delete newField.validation;

  return newField;
}

const emit = defineEmits<{
  turnstileToken: [token: string | null];
  turnstileVerified: [verified: boolean];
}>();

const { class: className } = useAttrs();

const handleTurnstileStatus = (_status: TurnstileStatusValue, token: string | null) => {
  emit('turnstileToken', token);
  emit('turnstileVerified', _status === TurnstileStatusValue.VERIFIED);
};

</script>

<template>
  <div v-if="form">
    <form :class="cn('text-left flex flex-col gap-16', className ?? '')">
      <slot name="startOfForm"/>

      <div v-for="(field, index) in dynamicFormSchema" class="mb-16">
        <component :is="field['component']" v-bind="bindFields(field)" :name="index"/>
      </div>
      <TurnstileFormShell @status-change="handleTurnstileStatus">
        <template #footer="{ verified }">
          <div>
            <slot name="endOfForm" />
          </div>
        </template>
      </TurnstileFormShell>
    </form>
  </div>
</template>
